export type RuntimeMessageKey =
	| "unsupportedFileType"
	| "notEnoughStorageFor"
	| "fileSize"
	| "fileSizeAvailable"
	| "cannotDecodeSafari"
	| "cannotDecode"
	| "cannotPreview"
	| "couldNotProcessVideo"
	| "couldNotProcess"
	| "failedProcess"
	| "failedSaveSound"
	| "failedRemoveSound"
	| "failedClearSounds"
	| "soundUnavailable"
	| "failedAddSound"
	| "notEnoughStorage"
	| "failedSaveProject"
	| "projectNotFound"
	| "projectsNotFound"
	| "someProjectsNotFound"
	| "failedRenameProject"
	| "failedDuplicateProjects"
	| "tryAgain"
	| "storageLimited"
	| "unknownError"
	| "emailSupport"
	| "changelogTitle"
	| "changelogSummary"
	| "viewChangelog"
	| "dismiss"
	| "uploadingMedia"
	| "uploadedNamedMedia"
	| "uploadedOneMedia"
	| "uploadedManyMedia"
	| "uploadedNoMedia"
	| "failedUploadMedia";

export type RuntimeMessageValues = Record<string, string | number>;

type RuntimeTranslator = ({
	key,
	values,
}: {
	key: RuntimeMessageKey;
	values?: RuntimeMessageValues;
}) => string;

const ENGLISH_FALLBACKS: Record<RuntimeMessageKey, string> = {
	unsupportedFileType: "Unsupported file type: {name}",
	notEnoughStorageFor: "Not enough browser storage for {name}",
	fileSize: "File size is {size}.",
	fileSizeAvailable:
		"File size is {size}, but only {available} is safely available in browser storage.",
	cannotDecodeSafari:
		"{codec} cannot be decoded in this browser, so this clip may not preview correctly. Convert it to H.264 MP4 or try importing it in Safari.",
	cannotDecode:
		"{codec} cannot be decoded in this browser, so this clip may not preview correctly. Convert it to H.264 MP4 and reimport it.",
	cannotPreview: "Can't preview {name}",
	couldNotProcessVideo: "Could not process video",
	couldNotProcess: "Couldn't process {name}",
	failedProcess: "Failed to process {name}",
	failedSaveSound: "Failed to save sound",
	failedRemoveSound: "Failed to remove sound",
	failedClearSounds: "Failed to clear saved sounds",
	soundUnavailable: "Sound file not available",
	failedAddSound: "Failed to add sound to timeline",
	notEnoughStorage: "Not enough browser storage",
	failedSaveProject: "Failed to save new project",
	projectNotFound: "Project not found",
	projectsNotFound: "Projects not found",
	someProjectsNotFound: "Some projects could not be found",
	failedRenameProject: "Failed to rename project",
	failedDuplicateProjects: "Failed to duplicate projects",
	tryAgain: "Please try again",
	storageLimited: "Storage is not fully supported. Some features may not work.",
	unknownError: "Unknown error",
	emailSupport: "Email support@editing.video",
	changelogTitle: "What's new in Editing.video",
	changelogSummary: "See the latest improvements and fixes.",
	viewChangelog: "See full changelog",
	dismiss: "Dismiss",
	uploadingMedia: "Uploading media...",
	uploadedNamedMedia: "{name} has been uploaded",
	uploadedOneMedia: "1 media asset has been uploaded",
	uploadedManyMedia: "{count} media assets have been uploaded",
	uploadedNoMedia: "No media assets were uploaded",
	failedUploadMedia: "Failed to upload media",
};

function interpolate({
	template,
	values,
}: {
	template: string;
	values?: RuntimeMessageValues;
}) {
	if (!values) return template;
	return template.replaceAll(/\{(\w+)\}/g, (match, key: string) =>
		Object.hasOwn(values, key) ? String(values[key]) : match,
	);
}

let activeTranslator: RuntimeTranslator = ({ key, values }) =>
	interpolate({ template: ENGLISH_FALLBACKS[key], values });

export function setRuntimeTranslator({
	translator,
}: {
	translator: RuntimeTranslator;
}) {
	const previousTranslator = activeTranslator;
	activeTranslator = translator;
	return () => {
		if (activeTranslator === translator) activeTranslator = previousTranslator;
	};
}

export function runtimeMessage({
	key,
	values,
}: {
	key: RuntimeMessageKey;
	values?: RuntimeMessageValues;
}) {
	return activeTranslator({ key, values });
}
